import { Injectable, signal } from '@angular/core';

import { STORAGE_KEYS } from '../../constants/storage-keys.const';
import { RESERVED_ROUTE_SEGMENTS } from '../../shared/routing/role-routing.util';
import { StorageUtil } from '../../utils/storage.utils';

@Injectable({ providedIn: 'root' })
export class TenantService {
  private initialized = false;
  private readonly tenantSignal = signal<string | null>(null);

  readonly tenantName = this.tenantSignal.asReadonly();

  /**
   * Resolves tenant once per page load (`provideAppInitializer`). Order: subdomain → storage.
   * Tenant is not encoded in URL paths — only the subdomain (e.g. `acme.localhost`).
   */
  initializeOnce(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    const fromSubdomain = this.extractFromSubdomain();
    if (fromSubdomain) {
      this.persistTenant(fromSubdomain);
      return;
    }

    const stored = StorageUtil.getLocal<string>(STORAGE_KEYS.TENANT_NAME);
    if (stored) {
      this.tenantSignal.set(this.normalizeSlug(stored));
    }
  }

  getTenantSlug(): string | null {
    return this.tenantSignal();
  }

  hasTenant(): boolean {
    return !!this.tenantSignal();
  }

  matchesRouteTenant(routeSlug: string): boolean {
    const current = this.tenantSignal();
    if (!current) {
      return false;
    }
    return current === this.normalizeSlug(routeSlug);
  }

  isAllowedSlug(slug: string): boolean {
    const normalized = this.normalizeSlug(slug);
    return !!normalized && !RESERVED_ROUTE_SEGMENTS.has(normalized);
  }

  clearTenant(): void {
    this.tenantSignal.set(null);
    StorageUtil.removeLocal(STORAGE_KEYS.TENANT_NAME);
    this.initialized = false;
  }

  private persistTenant(slug: string): void {
    const normalized = this.normalizeSlug(slug);
    if (!normalized) {
      return;
    }
    this.tenantSignal.set(normalized);
    StorageUtil.setLocal(STORAGE_KEYS.TENANT_NAME, normalized);
  }

  private normalizeSlug(slug: string): string {
    return slug.trim().toLowerCase();
  }

  private isValidSlug(slug: string | undefined): slug is string {
    return !!slug && !RESERVED_ROUTE_SEGMENTS.has(slug);
  }

  private extractFromSubdomain(): string | null {
    const host = globalThis.location?.hostname ?? '';
    if (!host || host === 'localhost' || host === '127.0.0.1') {
      return null;
    }

    const parts = host.split('.').filter(Boolean);
    if (parts.length === 0) {
      return null;
    }

    // sterling-logistics-in_1.localhost → sterling-logistics-in_1
    const tld = parts.at(-1);
    if (tld === 'localhost' && parts.length >= 2) {
      const slug = parts[0]?.toLowerCase();
      return this.isValidSlug(slug) ? slug : null;
    }

    // acme.admin.ews.example.com → acme
    if (parts.length >= 4 && parts[1] === 'admin') {
      const slug = parts.at(0)?.toLowerCase();
      return this.isValidSlug(slug) ? slug : null;
    }

    // tenant.example.com
    if (parts.length >= 3) {
      const slug = parts[0]?.toLowerCase();
      return this.isValidSlug(slug) ? slug : null;
    }

    return null;
  }

}
